<script setup>
import { ref, watch, onBeforeMount, nextTick, computed } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useDataCollectionStore } from "@/stores/dataCollection";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();
const uiStore = useUiStore();
const dataCollectionStore = useDataCollectionStore();
import AgentLayout from "@/views/layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import DBreadcrumb from "@/components/shared/DBreadcrumb.vue";
import FileSelectorModal from "@/components/dataCollection/FileSelectorModal.vue";
const companyId = useCompanyStore().companyId;

const currentStep = ref(1);
const id = ref(null);
const name = ref(null);
const description = ref(null);
const update = ref(false);
const breadcrumbItems = ref([]);
const errors = ref({});
// Campaign / Playlist State
const mappedFiles = ref([]);
const mappedUrls = ref([]);
const mappedIntegrations = ref([]);
const integrationOptions = ref([]);
const isSubmitted = ref(false);
const fileSelectorVisible = ref(false);
const activeRootType = ref(null);
const openSections = ref({ sources: true, docs: false, web: false, int: false });

const sourcesCount = computed(() => {
  return (
    mappedFiles.value.length +
    mappedUrls.value.length +
    mappedIntegrations.value.length
  );
});

const openFileSelectorModal = (rootType = null) => {
  if (rootType) {
    activeRootType.value = rootType;
  }
  fileSelectorVisible.value = true;
};

const handleFilesSelected = (files) => {
  let targetArray;
  // Determine which array to update based on activeRootType
  if (activeRootType.value === "urls") {
    targetArray = mappedUrls;
  } else if (activeRootType.value === "integrations") {
    targetArray = mappedIntegrations;
  } else {
    targetArray = mappedFiles;
  }
  // Add mode: add all selected files
  files.forEach((file) => {
    targetArray.value.push(file.dataItem);
  });

};

const closeFileSelectorModal = () => {
  fileSelectorVisible.value = false;
};

const removeFile = (index) => {
  mappedFiles.value.splice(index, 1);
};

const validate = () => {
  errors.value = {};
  let isValid = true;

  if (!name.value || !name.value.trim()) {
    errors.value.name = "Data Source name is required";
    isValid = false;
  }

  const hasContent =
    mappedFiles.value.length > 0 ||
    mappedUrls.value.length > 0 ||
    mappedIntegrations.value.length > 0;

  if (!hasContent) {
    errors.value.content =
      "At least one file, URL, or integration is required to create a data source.";
    isValid = false;
  }

  return isValid;
};

// Clear errors on input
watch(name, (val) => {
  if (val.trim()) {
    delete errors.value.name;
  }
});

watch(
  [mappedFiles, mappedUrls, mappedIntegrations],
  () => {
    const hasContent =
      mappedFiles.value.length > 0 ||
      mappedUrls.value.length > 0 ||
      mappedIntegrations.value.length > 0;
    if (hasContent) {
      delete errors.value.content;
    }
  },
  { deep: true },
);

// Step Navigation
const nextStep = () => {
  if (currentStep.value === 1) {
    if (!name.value || !name.value.trim()) {
      errors.value.name = "Data Source name is required";
      return;
    } else {
      delete errors.value.name;
    }
  }
  if (currentStep.value < 2) currentStep.value++;
};
const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--;
};

const goToDataSource = () => {
  router.push("/data-source");
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const createPayload = () => {
  const filesIds = mappedFiles.value.map((f) => f.id).filter(Boolean);
  const urlsIds = mappedUrls.value.map((u) => u.id).filter(Boolean);
  const integrationsIds = mappedIntegrations.value
    .map((i) => i.id)
    .filter(Boolean);

  let data = {};

  if (update.value) {
    data = {
      id: id.value,
      data: {
        company_id: companyId,
        name: name.value,
        description: description.value,
        files_ids: filesIds,
        urls_ids: urlsIds,
        integrations_ids: integrationsIds,
      },
    };
  } else {
    data = {
      company_id: companyId,
      name: name.value,
      description: description.value,
      files_ids: filesIds,
      urls_ids: urlsIds,
      integrations_ids: integrationsIds,
    };
  }

  return data;
};

const sendDataSource = (payload) => {
  const storeAction = update.value
    ? dataCollectionStore.editDataSource
    : dataCollectionStore.addDataSource;
  storeAction(payload)
    .then(() => {
      goToDataSource();
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};

const onSubmit = () => {
  if (!validate()) {
    if (errors.value.name) {
      currentStep.value = 1;
      nextTick(() => {
        const el = document.getElementById("data-source-name");
        if (el) el.focus();
      });
    } else if (errors.value.content) {
      nextTick(() => {
        const el = document.getElementById("source-content-error");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    return;
  }
  isSubmitted.value = true;

  if (mappedFiles.value.some((f) => f.file)) {
    currentStep.value = 2;
  }

  const payload = createPayload();
  sendDataSource(payload);
};

const cancel = () => {
  goToDataSource();
};

const fetchDataSourceById = async (id) => {
  dataCollectionStore
    .fetchDataSourceById({
      companyId,
      id,
    })
    .then((res) => {
      update.value = true;
      name.value = res.data.name;
      description.value = res.data.description ? res.data.description : null;
      mappedFiles.value = res.data.files;
      mappedFiles.value.forEach((file) => {
        file.file = null;
        file.updated = false;
      });

      mappedUrls.value = res.data.urls;
      mappedUrls.value.forEach((url) => {
        url.updated = false;
      });

      mappedIntegrations.value = res.data.integrations;
      mappedIntegrations.value.forEach((integration) => {
        integration.updated = false;
        integrationOptions.value.map((i) => {
          if (i.id === integration.id) {
            integration.integration = i;
          }
        });
      });

      breadcrumbItems.value = [
        { text: "Data Source", to: { name: "DataSourceView" } },
        { text: res.data.name, active: true },
      ];
    })
    .catch(() => {});
};
const resetStoreData = () => {
  uiStore.setModalState(false);
  uiStore.setModalData({});
  uiStore.setModalAction(null);
};

onBeforeMount(async () => {
  resetStoreData();
  if (route.params.id) {
    id.value = route.params.id;
    await fetchDataSourceById(route.params.id);
  }
});
</script>

<template>
  <AgentLayout>
    <!-- File Selector Modal -->
    <FileSelectorModal
      :visible="fileSelectorVisible"
      @update:visible="fileSelectorVisible = $event"
      :root-type="activeRootType"
      @files-selected="handleFilesSelected"
      @close="closeFileSelectorModal"
    />
    <DBreadcrumb v-if="update" :items="breadcrumbItems" />
    <PageHeader
      :title="update ? 'Edit Data Source' : 'Create New Data Source'"
      subtitle="Manage your data sources"
      :showButton="false"
    />
    <div class="create-ad-container card">
      <!-- Wizard Tabs Header -->
      <div class="wizard-tabs">
        <div
          class="tab-item"
          :class="{ active: currentStep === 1, completed: currentStep > 1 }"
          @click="currentStep = 1"
        >
          <span class="tab-number">1</span>
          <span class="tab-label">Data Source Setup</span>
        </div>
        <div class="tab-line"></div>
        <div
          class="tab-item"
          :class="{ active: currentStep === 2, completed: currentStep > 2 }"
          @click="currentStep = 2"
        >
          <span class="tab-number">2</span>
          <span class="tab-label">Data Source Details</span>
        </div>
        <!-- <div class="tab-line"></div> -->
        <!-- <div
          class="tab-item"
          :class="{ active: currentStep === 3, completed: currentStep > 3 }"
          @click="currentStep = 3"
        >
          <span class="tab-number">3</span>
          <span class="tab-label">Urls</span>
        </div> -->
        <!-- <div class="tab-line"></div> -->
        <!-- <div
          class="tab-item"
          :class="{ active: currentStep === 4, completed: currentStep > 4 }"
          @click="currentStep = 4"
        >
          <span class="tab-number">4</span>
          <span class="tab-label">Integrations</span>
        </div> -->
      </div>

      <!-- Main Content Area -->
      <div class="wizard-content">
        <!-- STEP 1: Data Source Setup -->
        <div v-show="currentStep === 1" class="datasource-setup-layout">
          <div class="step-header">
            <h2 class="step-title">Data Source Details</h2>
          </div>

          <div class="form-group mb-3">
            <DInput
              label="Data Source Name"
              :value="name"
              @input="(e) => { name = e.target.value }"
              type="text"
              width="100%"
              :class="{ 'is-invalid': errors.name }"
            />
            <span v-if="errors.name" class="text-danger mt-1 block text-sm">{{ errors.name }}</span>
          </div>

          <FloatLabel style="display: block; width: 100%;" class="mb-3 form-group">
            <PTextarea
              v-model="description"
              id="dataSourceDescription"
              class="d-textarea"
              style="width: 100%;"
              rows="8"
              autoResize
            />
            <label for="dataSourceDescription">Description (Optional)</label>
          </FloatLabel>
        </div>

        <!-- STEP 2: Files, URLs, Integrations (Unified) -->
        <div v-show="currentStep === 2" class="assignments-layout">
          <div class="files-wrapper w-full">
            <!-- Add Data Sources -->
            <h5 class="mb-3 flex items-center gap-2">
              <span class="text-warning">●</span> Quick Add Data Sources
            </h5>
            <div class="grid grid-cols-12 gap-3 mb-4">
              <!-- Documents Card -->
              <div class="col-span-12 md:col-span-4">
                <div
                  class="card h-full source-card flex flex-row items-center p-4 gap-3"
                  style="
                    border-style: solid;
                    border-width: 1px;
                    cursor: pointer;
                  "
                  @click="openFileSelectorModal('documents')"
                >
                  <div class="">
                    <span
                      class="p-3 bg-white rounded-full shadow-sm inline-block"
                    >
                      <i class="bi bi-file-earmark-text text-warning fs-4"></i>
                    </span>
                  </div>
                  <div class="text-start">
                    <h6 class="font-bold mb-1">Documents</h6>
                    <p class="text-gray-500 small mb-0">PDF, CSV</p>
                  </div>
                  <div class="ml-auto">
                    <i class="bi bi-plus-lg fs-4 text-gray-500"></i>
                  </div>
                </div>
              </div>

              <!-- Website Card -->
              <div class="col-span-12 md:col-span-4">
                <div
                  class="card h-full source-card flex flex-row items-center p-4 gap-3"
                  style="cursor: pointer"
                  @click="openFileSelectorModal('urls')"
                >
                  <div class="">
                    <span
                      class="p-3 bg-white rounded-full shadow-sm inline-block"
                    >
                      <i class="bi bi-globe text-primary fs-4"></i>
                    </span>
                  </div>
                  <div class="text-start">
                    <h6 class="font-bold mb-1">Website</h6>
                    <p class="text-gray-500 small mb-0">
                      Blog posts, articles & portfolio
                    </p>
                  </div>
                  <div class="ml-auto">
                    <i class="bi bi-plus-lg fs-4 text-gray-500"></i>
                  </div>
                </div>
              </div>

              <!-- Integrations Card (Generic) -->
              <div class="col-span-12 md:col-span-4">
                <div
                  class="card h-full source-card flex flex-row items-center p-4 gap-3"
                  style="cursor: pointer"
                  @click="openFileSelectorModal('integrations')"
                >
                  <div class="">
                    <span
                      class="p-3 bg-white rounded-full shadow-sm inline-block"
                    >
                      <i class="bi bi-puzzle text-info fs-4"></i>
                    </span>
                  </div>
                  <div class="text-start">
                    <h6 class="font-bold mb-1">Integrations</h6>
                    <p class="text-gray-500 small mb-0">Connect external apps</p>
                  </div>
                  <div class="ml-auto">
                    <i class="bi bi-plus-lg fs-4 text-gray-500"></i>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="errors.content"
              id="source-content-error"
              class="alert alert-danger mt-4 flex items-center justify-center"
              role="alert"
            >
              <i class="bi bi-exclamation-triangle-fill mr-2"></i>
              <div>
                {{ errors.content }}
              </div>
            </div>

            <!-- Sources Accordion -->
            <div class="sources-accordion">
              <div
                class="sources-accordion__header"
                id="sourcesHeading"
                :class="{ 'is-open': openSections.sources }"
                @click="openSections.sources = !openSections.sources"
              >
                <div class="flex items-center gap-3">
                  <span class="sources-accordion__icon sources-accordion__icon--warning">
                    <i class="bi bi-database"></i>
                  </span>
                  <h5 class="sources-accordion__title">{{ sourcesCount }} Sources</h5>
                </div>
                <div class="flex items-center gap-2">
                  <span class="sources-accordion__count">{{ sourcesCount }}</span>
                  <i class="bi bi-chevron-down sources-accordion__chevron"></i>
                </div>
              </div>

              <div v-show="openSections.sources">
                <div class="sources-accordion__body">
                  <p class="sources-accordion__hint">
                    Click to manage your knowledge sources
                  </p>

                  <!-- Categories -->
                  <div class="sources-categories" id="categoriesAccordion">
                    <!-- Documents -->
                    <div class="sources-category">
                      <h2 class="sources-category__header" id="headingDocs">
                        <button
                          class="sources-category__button"
                          :class="{ 'is-open': openSections.docs }"
                          type="button"
                          @click="openSections.docs = !openSections.docs"
                        >
                          <span class="sources-category__icon sources-category__icon--warning">
                            <i class="bi bi-file-earmark-text"></i>
                          </span>
                          <span class="sources-category__label">Document</span>
                          <span class="sources-category__count">{{ mappedFiles.length }}</span>
                          <i class="bi bi-chevron-down sources-category__chevron"></i>
                        </button>
                      </h2>
                      <div
                        v-show="openSections.docs"
                        class="sources-category__panel"
                      >
                        <div class="sources-category__body">
                          <!-- File List -->
                          <div
                            v-for="(file, idx) in mappedFiles"
                            :key="idx"
                            class="sources-item"
                          >
                            <div class="flex items-center gap-2">
                              <div class="sources-item__icon sources-item__icon--danger">
                                <i
                                  v-if="file.mime_type === 'application/pdf' || file.mime_type === 'application/x-pdf' || file.mime_type === 'pdf'"
                                  class="bi bi-file-pdf text-danger"
                                ></i>
                                <i
                                  v-if="
                                    file.mime_type ===
                                    'application/vnd.ms-excel'
                                  "
                                  class="bi bi-file-excel text-success"
                                ></i>
                                <i
                                  v-if="file.mime_type === 'application/msword'"
                                  class="bi bi-file-earmark text-primary"
                                ></i>
                                <i
                                  v-if="
                                    file.mime_type ===
                                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                  "
                                  class="bi bi-file-earmark text-primary"
                                ></i>
                                <i
                                  v-if="
                                    file.mime_type ===
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                  "
                                  class="bi bi-file-excel text-success"
                                ></i>
                                <i
                                  v-if="
                                    file.mime_type ===
                                    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                                  "
                                  class="bi bi-file-excel text-success"
                                ></i>
                                <i
                                  v-if="file.mime_type === 'text/csv'"
                                  class="bi bi-filetype-csv text-success"
                                ></i>
                              </div>
                              <div>
                                <div class="sources-item__name">
                                  {{ file.name }}
                                </div>
                                <div class="sources-item__meta text-uppercase">
                                  {{ file.file_type || "FILE" }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="file.created_at"
                              class="sources-item__meta sources-item__meta--date"
                            >
                              {{ formatDate(file.created_at) }}
                            </div>
                            <button
                              class="sources-item__remove"
                              @click="removeFile(idx)"
                            >
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                          <div
                            v-if="mappedFiles.length === 0"
                            class="sources-empty"
                          >
                            No documents added.
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Websites -->
                    <div class="sources-category">
                      <h2 class="sources-category__header" id="headingWeb">
                        <button
                          class="sources-category__button"
                          :class="{ 'is-open': openSections.web }"
                          type="button"
                          @click="openSections.web = !openSections.web"
                        >
                          <span class="sources-category__icon sources-category__icon--primary">
                            <i class="bi bi-globe"></i>
                          </span>
                          <span class="sources-category__label">Websites</span>
                          <span class="sources-category__count">{{ mappedUrls.length }}</span>
                          <i class="bi bi-chevron-down sources-category__chevron"></i>
                        </button>
                      </h2>
                      <div
                        v-show="openSections.web"
                        class="sources-category__panel"
                      >
                        <div class="sources-category__body">
                          <div
                            v-for="(url, idx) in mappedUrls"
                            :key="idx"
                            class="sources-item"
                          >
                            <div class="flex items-center gap-2">
                              <div class="sources-item__icon sources-item__icon--primary">
                                <i class="bi bi-link-45deg"></i>
                              </div>
                              <div>
                                <div class="sources-item__name">
                                  {{ url.name }}
                                </div>
                                <div class="sources-item__meta">
                                  {{ url.url }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="url.created_at"
                              class="sources-item__meta sources-item__meta--date"
                            >
                              {{ formatDate(url.created_at) }}
                            </div>
                            <button
                              class="sources-item__remove"
                              @click="mappedUrls.splice(idx, 1)"
                            >
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                          <div
                            v-if="mappedUrls.length === 0"
                            class="sources-empty"
                          >
                            No websites added.
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Integrations -->
                    <div class="sources-category">
                      <h2 class="sources-category__header" id="headingInt">
                        <button
                          class="sources-category__button"
                          :class="{ 'is-open': openSections.int }"
                          type="button"
                          @click="openSections.int = !openSections.int"
                        >
                          <span class="sources-category__icon sources-category__icon--info">
                            <i class="bi bi-puzzle"></i>
                          </span>
                          <span class="sources-category__label">Integrations</span>
                          <span class="sources-category__count">{{ mappedIntegrations.length }}</span>
                          <i class="bi bi-chevron-down sources-category__chevron"></i>
                        </button>
                      </h2>
                      <div
                        v-show="openSections.int"
                        class="sources-category__panel"
                      >
                        <div class="sources-category__body">
                          <div
                            v-for="(int, idx) in mappedIntegrations"
                            :key="idx"
                            class="sources-item"
                          >
                            <div class="flex items-center gap-2">
                              <div class="sources-item__icon sources-item__icon--success">
                                <i class="bi bi-plug"></i>
                              </div>
                              <div>
                                <div class="sources-item__name">
                                  {{ int.name }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="int.created_at"
                              class="sources-item__meta sources-item__meta--date"
                            >
                              {{ formatDate(int.created_at) }}
                            </div>
                            <button
                              class="sources-item__remove"
                              @click="mappedIntegrations.splice(idx, 1)"
                            >
                              <i class="bi bi-trash"></i>
                            </button>
                          </div>
                          <div
                            v-if="mappedIntegrations.length === 0"
                            class="sources-empty"
                          >
                            No integrations added.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- STEP 3: URLs -->
        <!-- <div v-show="currentStep === 3" class="assignments-layout">
          <div class="files-wrapper">
            <div
              class="step-header flex justify-between items-center"
            >
              <div>
                <h2 class="step-title">Add URLs to Data Source</h2>
                <p class="step-subtitle mb-0">
                  Provide labels and addresses for the websites you want to
                  index.
                </p>
              </div>
            </div>

            <div class="integrations-section mt-4">
              <div
                v-if="mappedUrls.length === 0"
                class="empty-state mb-3"
                :class="{ 'border-danger': errors.content }"
              >
                <p class="mb-0">No URLs added yet.</p>
              </div>
              <p v-if="errors.content" class="error-msg-section">
                {{ errors.content }}
              </p>
              <div
                v-for="(item, index) in mappedUrls"
                :key="index"
                class="integration-card"
              >
                <div class="integration-inputs">
                  <div class="form-group grow">
                    <label>URL Label</label>
                    <input
                      v-model="item.name"
                      type="text"
                      class="std-input"
                      placeholder="e.g. Documentation"
                      @input="markItemUpdated(item)"
                    />
                  </div>
                  <div class="form-group grow">
                    <label>URL</label>
                    <input
                      v-model="item.url"
                      type="text"
                      class="std-input"
                      placeholder="https://example.com"
                      @input="markItemUpdated(item)"
                    />
                  </div>
                </div>
                <div class="integration-actions">
                  <button class="remove-btn" @click="removeUrl(index)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>

              <button class="dashed-add-btn" @click="addNewUrl">
                <i class="dx-icon-add"></i> Add URL
              </button>
            </div>
          </div>
        </div> -->

        <!-- STEP 4: Integrations -->
        <!-- <div v-show="currentStep === 4" class="assignments-layout">
          <div class="files-wrapper">
            <div class="step-header">
              <h2 class="step-title">Integrations Configuration</h2>
              <p class="step-subtitle">
                Connect multiple services to enrich your knowledge base with
                automated data flows.
              </p>
            </div>

            <div class="integrations-section mt-4">
              <div
                v-if="mappedIntegrations.length === 0"
                class="empty-state mb-3"
                :class="{ 'border-danger': errors.content }"
              >
                <p class="mb-0">No integrations added yet.</p>
              </div>
              <p v-if="errors.content" class="error-msg-section">
                {{ errors.content }}
              </p>
              <div
                v-for="(item, index) in mappedIntegrations"
                :key="index"
                class="integration-card"
              >
                <div class="integration-inputs">
                  <div class="form-group grow">
                    <label>Integration Name</label>
                    <input
                      v-model="item.name"
                      type="text"
                      class="std-input"
                      placeholder="Enter integration name"
                      @input="markItemUpdated(item)"
                    />
                  </div>
                  <div class="form-group grow">
                    <label>Select Integration</label>

                    <div class="relative mb-3">
                      <span
                        v-if="item.integration"
                        class="multiselect-title-span"
                        >Integration</span
                      >

                      <VMultiselect
                        v-model="item.integration"
                        :options="integrationOptions"
                        :disabled="isLoadingIntegrations"
                        :loading="isLoadingIntegrations"
                        :searchable="true"
                        :close-on-select="true"
                        :show-labels="false"
                        placeholder="Select integration"
                        trackBy="id"
                        label="name"
                        @select="markItemUpdated(item)"
                      />
                    </div>
                  </div>
                </div>
                <div class="integration-actions">
                  <button class="remove-btn" @click="removeIntegration(index)">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>

              <button class="dashed-add-btn" @click="addNewIntegration">
                <i class="dx-icon-add"></i> Add Integration
              </button>
            </div>
          </div>
        </div> -->
      </div>

      <!-- Footer Actions -->
      <div class="actions-footer">
        <button
          class="btn btn-secondary"
          @click="cancel"
          :disabled="isSubmitted"
        >
          Cancel
        </button>

        <div class="right-actions">
          <!-- Step 1 Nav -->
          <button
            v-if="currentStep === 2"
            class="btn btn-secondary"
            @click="prevStep"
            :disabled="isSubmitted"
          >
            <i class="bi bi-arrow-left"></i>
            Back
          </button>
          <div v-if="currentStep === 2" class="separator-v"></div>
          <button
            v-if="currentStep === 1"
            class="btn btn-primary"
            @click="nextStep"
            :disabled="isSubmitted"
          >
            Next <i class="bi bi-arrow-right"></i>
          </button>

          <!-- Step 2 Nav -->
          <template v-if="currentStep === 2">
            <!-- <div class="separator-v"></div> -->
            <!-- <button
              class="btn btn-primary-outline"
              @click="saveDraft"
              :disabled="isSubmitted"
            >
              <i class="bi bi-save"></i> Save Draft
            </button> -->
            <button
              class="btn btn-primary"
              @click="onSubmit"
              :disabled="isSubmitted"
            >
              Submit
            </button>
          </template>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style lang="scss" scoped>
/* Reset and Base Vars */
.create-ad-container {
  color: var(--text-primary);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Layout */
.ad-editor-layout {
  display: flex;
  height: 100%;
  width: 100%;
}
.config-panel {
  flex: 3;
  padding: 2rem;
  overflow-y: auto;
  border-right: 1px solid var(--border-color);
}
.datasource-setup-layout {
  width: 100%;
  padding: 2rem;
  overflow-y: auto;
  box-sizing: border-box;
}
.preview-panel {
  flex: 2;
  background-color: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  border-left: 1px solid var(--border-color);
  max-width: 450px;
}

/* Assignments Layout */
.assignments-layout {
  height: 100%;
  overflow-y: auto;
  background: white;
  padding: 2rem;
  // display: flex;
  justify-content: center;
}
.assignments-container {
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 1000px;
  height: fit-content;
}
.assignments-header {
  margin-bottom: 2rem;
  h2 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }
}
.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}
.location-card {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  background: white;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }

  &.selected {
    border-color: var(--primary-color);
    background: #f8fbff;

    .checkbox-circle {
      background: var(--primary-color);
      border-color: var(--primary-color);
      color: white;
    }
  }
}
.checkbox-circle {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.2s;
}
.loc-icon {
  font-size: 1.5rem;
  color: var(--text-secondary);
}
.loc-details h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}
.loc-group {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* Common Components */
.step-section {
  margin-bottom: 2.5rem;
}
.step-header {
  margin-bottom: 1rem;
}
.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

/* Format Cards */
.format-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.format-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  background-color: white;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  &.active {
    border: 2px solid var(--primary-color);
    background-color: #f8fbff;
    padding: calc(1.25rem - 1px) calc(1.5rem - 1px);
  }
}
.card-content {
  flex: 1;
}
.card-content h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.card-content p {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
}

.visual-indicator {
  width: 120px;
  height: 60px;
  border-radius: 6px;
  background-color: #e2e8f0;
  margin-left: 2rem;
  flex-shrink: 0;
}
.screensaver-visual {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
}
.topbar-visual {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  position: relative;
}
.topbar-visual::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 12px;
  background: var(--primary-color);
}
.slider-visual {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  position: relative;
}
.slider-visual::after {
  content: "";
  position: absolute;
  top: 12px;
  left: 0;
  right: 0;
  height: 24px;
  background: var(--primary-color);
}

/* Forms */
.form-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}
.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
}
.is-invalid {
  border-color: #dc3444 !important;
}

/* Playlist */
.assets-container {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  background: #fff;
}
.empty-playlist {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  color: var(--text-secondary);
  i {
    font-size: 2rem;
  }
}
.playlist-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}
.playlist-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.playlist-item {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid var(--border-color);
  padding: 0.75rem;
  border-radius: 8px;
  gap: 1rem;
  cursor: grab;
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &.dragging {
    opacity: 0.5;
    background: #f8fbff;
  }

  &:hover {
    border-color: var(--primary-color);
  }
}
.item-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 2;
}
.item-thumb {
  width: 60px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
}
.item-info h4 {
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
  max-width: 300px;
}
.badge {
  font-size: 0.75rem;
  background: var(--bg-secondary);
  padding: 2px 6px;
  border-radius: 4px;
}
.item-config {
  flex: 2;
}
.config-field label {
  display: block;
  font-size: 0.75rem;
  color: var(--text-secondary);
  // margin-bottom: 0.25rem;
}
.small-input {
  padding: 0.4rem;
  font-size: 0.85rem;
}
.input-with-suffix {
  position: relative;
  display: flex;
  align-items: center;
}
.suffix {
  position: absolute;
  right: 8px;
  color: var(--text-secondary);
  font-size: 0.8rem;
}
.remove-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  &:hover {
    color: var(--danger-color);
  }
}

/* Footer */
.actions-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  background: white;
  border-top: 1px solid var(--border-color);
  z-index: 10;
}
.right-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}
.separator-v {
  height: 24px;
  width: 1px;
  background: var(--border-color);
  margin: 0 0.5rem;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

/* Files Step Styles */
.files-wrapper {
  //   max-width: 900px;
  width: 100%;
}
.step-subtitle {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-top: -0.5rem;
  margin-bottom: 2rem;
}
.upload-row {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.file-upload-zone {
  border: 1px dashed var(--border-color);
  border-radius: 8px;
  padding: 0.6rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  background: white;
  color: var(--text-secondary);
  transition: all 0.2s;
  height: 42px; /* Match input height roughly */

  &:hover {
    border-color: var(--primary-color);
    background: #f1f5f9;
  }
  &.has-file {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: #eff6ff;
  }
}
.file-source-info {
  border: 1px solid #10b981;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ecfdf5;
  color: #047857;
  height: 42px;
  font-size: 0.875rem;

  i {
    font-size: 1.2rem;
  }
}
.hidden-input {
  display: none;
}
.btn-icon {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #f1f5f9;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #e2e8f0;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.add-more-link {
  margin-bottom: 2rem;
}
.text-btn {
  background: none;
  border: none;
  padding: 0;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &.success {
    color: #10b981;
    &:hover {
      text-decoration: underline;
    }
  }
}

/* Custom Table */
.custom-table-wrapper {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
}
.custom-table {
  width: 100%;
  border-collapse: collapse;
  background: white;

  th,
  td {
    padding: 1rem 1.5rem;
    text-align: left;
  }

  thead {
    background: #f8fafc;
    border-bottom: 1px solid var(--border-color);
    th {
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  tbody tr {
    border-bottom: 1px solid var(--border-color);
    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #f8fafc;
    }
  }
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
.text-gray-500 {
  color: var(--text-secondary);
  font-size: 0.9rem;
}
.font-weight-bold {
  font-weight: 600;
  color: var(--text-primary);
}
.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;

  &.ready {
    background: #dcfce7;
    color: #166534;
    &.danger:hover {
      color: var(--danger-color);
    }
  }

  &.processing {
    background: #dbeafe;
    color: var(--primary-hover);
  }
}

/* URLs Section */
/* URLs Section */
.step-badge {
  background: #eff6ff;
  color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  border: 1px solid #dbeafe;
}
.dashed-add-btn {
  width: 100%;
  border: 1px dashed #bfdbfe;
  background: #f8fbff;
  color: var(--primary-color);
  padding: 1rem;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 1.2rem;
  }

  &:hover {
    background: #eff6ff;
    border-color: var(--primary-color);
  }
}

/* Integrations Section */
.integrations-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.integration-card {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s;
}
.integration-inputs {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.integration-input-with-details {
  grid-template-columns: 1fr !important;
}

.file-details-info {
  grid-column: 1 / -1;
  border-top: 1px solid #e2e8f0;
  padding-top: 0.75rem;
  margin-top: -0.5rem;
}

.progress-info {
  grid-column: 1 / -1;
}

.view-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.integration-actions {
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 1.8rem; /* Align with inputs */
}

.remove-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.2s;
  opacity: 0.6;

  &:hover {
    color: var(--danger-color);
    opacity: 1;
  }
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  transition: color 0.2s;

  &.danger:hover {
    color: var(--danger-color);
  }
}
.empty-state {
  text-align: center;
  padding: 3rem;
  background: #f8fafc;
  border-radius: 8px;
  color: var(--text-secondary);
  border: 1px dashed var(--border-color);
}
.text-right {
  text-align: right;
}

.btn-primary-outline {
  border: 1px solid var(--primary-color);
  background: white;
  color: var(--primary-color);
  &:hover {
    background: #eff6ff;
  }
}
.btn-sm {
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
}
.dashed-btn {
  border: 1px dashed var(--border-color);
  // background: transparent;
  border-color: var(--primary-color);
  background: #f8fbff;
  color: var(--text-primary);
  &:hover {
    border-color: var(--primary-color);
    background: #f8fbff;
    color: var(--primary-color);
  }
}

/* Preview */
.preview-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: auto;
}
.preview-header h3 {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: 1px;
}
.device-frame {
  width: 300px;
  height: 533px;
  background: black;
  border-radius: 30px;
  border: 8px solid #333;
  overflow: hidden;
  margin: 2rem 0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}
.screen-content {
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.preview-img-full {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.preview-overlay-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem;
}
.ad-placeholder-content {
  color: white;
  text-align: center;
  p {
    opacity: 0.7;
  }
}
.preview-note {
  margin-top: auto;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: white;
  width: 800px;
  max-width: 90%;
  height: 80%;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
}
.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3 {
    margin: 0;
  }
}
.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}
.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}
.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &.selected {
    border-color: var(--primary-color);
  }
  .selection-check {
    position: absolute;
    top: 8px;
    right: 8px;
    background: var(--primary-color);
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .media-name {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 0.75rem;
    padding: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Transitions */
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}


.border-danger {
  border: 1px solid var(--danger-color) !important;
}

.error-msg-section {
  color: var(--danger-color);
  font-size: 0.875rem;
}

.source-card {
  &:hover {
    border-color: var(--primary-color) !important;
    transform: translateY(-4px);
    background-color: #f8fbff;
  }
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .actions-footer {
  background: #161c2c;
  border-top-color: #2d3f5a;
}

[data-theme="dark"] .assignments-layout {
  background: #0d1117;
}

[data-theme="dark"] .separator-v {
  background: #2d3f5a;
}

[data-theme="dark"] .source-card {
  &:hover {
    background-color: #1a2d52 !important;
  }
}

/* ── Sources Accordion ─────────────────────────────────────── */
.sources-accordion {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.sources-accordion__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.18s ease;

  &:hover {
    background: #f8fafc;
  }

  &.is-open {
    border-bottom: 1px solid #eef2f7;
  }
}

.sources-accordion__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &--warning {
    background: #fff8e1;
    color: #f59e0b;
  }
}

.sources-accordion__title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.sources-accordion__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.sources-accordion__chevron {
  color: #94a3b8;
  font-size: 14px;
  transition: transform 0.2s ease;

  .is-open & {
    transform: rotate(180deg);
  }
}

.sources-accordion__body {
  padding: 0 20px 20px;
}

.sources-accordion__hint {
  margin: 14px 0;
  font-size: 0.8125rem;
  color: #64748b;
}

/* Categories */
.sources-categories {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sources-category {
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  overflow: hidden;
  transition: border-color 0.18s ease, background-color 0.18s ease;

  &:hover {
    border-color: #dbeafe;
  }
}

.sources-category__header {
  margin: 0;
}

.sources-category__button {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  color: #0f172a;
  transition: background-color 0.18s ease;

  &:hover {
    background: rgba(241, 245, 249, 0.6);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color, var(--primary-color));
    outline-offset: -2px;
  }
}

.sources-category__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;

  &--warning {
    background: #fff8e1;
    color: #f59e0b;
  }

  &--primary {
    background: #e0ecff;
    color: var(--primary-color);
  }

  &--info {
    background: #e0f2fe;
    color: #0ea5e9;
  }
}

.sources-category__label {
  font-weight: 500;
  color: #0f172a;
  flex: 1;
}

.sources-category__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
}

.sources-category__chevron {
  color: #94a3b8;
  font-size: 12px;
  transition: transform 0.2s ease;

  .is-open & {
    transform: rotate(180deg);
  }
}

.sources-category__panel {
  border-top: 1px solid #eef2f7;
  background: #ffffff;
}

.sources-category__body {
  padding: 4px 12px;
}

/* Items */
.sources-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: 0;
  }
}

.sources-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &--danger {
    background: #fee2e2;
    color: #ef4444;
  }

  &--primary {
    background: #e0ecff;
    color: var(--primary-color);
  }

  &--success {
    background: #dcfce7;
    color: #16a34a;
  }
}

.sources-item__name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
}

.sources-item__meta {
  font-size: 0.75rem;
  color: #64748b;

  &--date {
    margin-left: auto;
    margin-right: 12px;
  }
}

.sources-item__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid transparent;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: #fee2e2;
    border-color: #fecaca;
    color: #dc2626;
  }
}

.sources-empty {
  text-align: center;
  font-size: 0.8125rem;
  color: #94a3b8;
  padding: 14px 0;
}

/* ── Sources Accordion · Dark mode ─────────────────────────── */
[data-theme="dark"] {
  .sources-accordion {
    background: #161c2c;
    border-color: #2d3f5a;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }

  .sources-accordion__header {
    &:hover {
      background: #1a2238;
    }

    &.is-open {
      border-bottom-color: #2d3f5a;
    }
  }

  .sources-accordion__icon--warning {
    background: rgba(245, 158, 11, 0.18);
    color: #fbbf24;
  }

  .sources-accordion__title {
    color: #e8edf5;
  }

  .sources-accordion__count {
    background: #1e2a3e;
    border-color: #3d5070;
    color: #9aadc4;
  }

  .sources-accordion__chevron {
    color: #6480a0;
  }

  .sources-accordion__hint {
    color: #9aadc4;
  }

  .sources-category {
    background: #1e2a3e;
    border-color: #2d3f5a;

    &:hover {
      border-color: #4e7fff;
    }
  }

  .sources-category__button {
    color: #e8edf5;

    &:hover {
      background: rgba(78, 127, 255, 0.08);
    }
  }

  .sources-category__icon--warning {
    background: rgba(245, 158, 11, 0.18);
    color: #fbbf24;
  }

  .sources-category__icon--primary {
    background: rgba(78, 127, 255, 0.2);
    color: #6b96ff;
  }

  .sources-category__icon--info {
    background: rgba(14, 165, 233, 0.2);
    color: #38bdf8;
  }

  .sources-category__label {
    color: #e8edf5;
  }

  .sources-category__count {
    background: #28364e;
    border-color: #3d5070;
    color: #9aadc4;
  }

  .sources-category__chevron {
    color: #6480a0;
  }

  .sources-category__panel {
    background: #161c2c;
    border-top-color: #2d3f5a;
  }

  .sources-item {
    border-bottom-color: #2d3f5a;
  }

  .sources-item__icon--danger {
    background: rgba(248, 113, 113, 0.18);
    color: #f87171;
  }

  .sources-item__icon--primary {
    background: rgba(78, 127, 255, 0.2);
    color: #6b96ff;
  }

  .sources-item__icon--success {
    background: rgba(34, 197, 94, 0.18);
    color: #4ade80;
  }

  .sources-item__name {
    color: #e8edf5;
  }

  .sources-item__meta {
    color: #9aadc4;
  }

  .sources-item__remove {
    color: #6480a0;

    &:hover {
      background: rgba(248, 113, 113, 0.18);
      border-color: rgba(248, 113, 113, 0.5);
      color: #fca5a5;
    }
  }

  .sources-empty {
    color: #6480a0;
  }
}
</style>
